import { Component, OnInit, ViewChild } from '@angular/core'
import { ParkingService } from '../../../services/parking.service'
import { Parking, parkingsPath, ParkingStatus, ParkingType, Term } from '@parkupp/core'
import { ActivatedRoute, Router } from '@angular/router'
import { LatLngBounds } from '@agm/core/services/google-maps-types'
import { distanceBetween, geohashQueryBounds } from 'geofire-common'
import { collection, endAt, Firestore, onSnapshot, orderBy, query, startAt } from '@angular/fire/firestore'
import { GoogleMapComponent } from '../../core/google-map/google-map.component'
import { environment } from '../../../../environments/environment'
import { Meta, Title } from '@angular/platform-browser'

@Component({
    selector: 'app-parking-map-search-page',
    templateUrl: './parking-map-search-page.component.html',
    styleUrls: ['./parking-map-search-page.component.scss'],
})
export class ParkingMapSearchPageComponent implements OnInit {
    [x: string]: any

    maxRadiusInMeters = 1000000
    parkings: Parking[]
    autoCompleteInputElement: any
    geoLocation: any
    displayParkingsWithStatus = [ParkingStatus.PUBLISHED]
    term: Term | null = Term.MONTHLY
    parkingKeyHighlighted: string
    terms = Term
    selectedType: ParkingType | null
    parkingTypes = ParkingType
    currentBounds: LatLngBounds
    showMap = false
    zoom = 5

    @ViewChild(GoogleMapComponent) googleMapComponent: GoogleMapComponent

    constructor(
        private parkingSvc: ParkingService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private firestore: Firestore,
        private titleService: Title,
        private metaTagService: Meta
    ) {
        this.titleService.setTitle('ParkUpp - Map Search')
        this.metaTagService.updateTag({ name: 'description', content: 'Search for available parking near me using the map view' })
    }

    ngOnInit(): void {
        if (window.navigator.geolocation) {
            window.navigator.geolocation.getCurrentPosition((location) => {
                this.googleMapComponent.zoom = 11
                this.googleMapComponent.updateMapLocation(location.coords.latitude, location.coords.longitude)
            })
        }

        if (window.innerWidth < 768) {
            this.autoCompleteInputElement = document.getElementById('geo-search-mobile')
        } else {
            this.autoCompleteInputElement = document.getElementById('geo-search')
        }

        // This code filters the parkings by type
        // this.activatedRoute.queryParams.forEach((queryParams) => {
        //     if (queryParams['parkingType']) {
        //         this.onTypeChange(queryParams['parkingType'])
        //     } else {
        //         this.selectedType = this.parkingTypes.STANDARD
        //     }
        // })
    }

    onToggleMap() {
        this.showMap = !this.showMap
    }

    onDisplayParkings(parkings: Parking[]) {
        this.parkings = parkings
        // Randomise the parkings
        this.parkings = this.parkings.sort(() => Math.random() - 0.5)
        this.googleMapComponent.generateMarkers(this.parkings, this.term)
    }

    onParkingMarkerSelected(parkingKey: string) {
        this.parkingKeyHighlighted = parkingKey
        this.showMap = false
        document.getElementById('list-item-' + parkingKey)!!.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    }

    onSelect(parking: Parking) {
        // window.open(`${environment.host}/parking/${parking.$key}`, '_blank')
    }

    onChangeLocation($event: any) {
        this.geoLocation = $event.parse()
    }

    onTermSelected(term: Term) {
        this.term = term
        this.onBoundsChange(this.currentBounds)
    }

    onTypeChange(parkingType: ParkingType | null) {
        this.selectedType = parkingType

        if (this.selectedType == ParkingType.PARKING_EVENTS) {
            this.term = Term.ONCEOFF
        }

        if (this.currentBounds) {
            this.onBoundsChange(this.currentBounds)
        }
        return false
    }

    onHover(parking: Parking | null) {
        if (parking) {
            this.googleMapComponent.setHoverParkingKey(parking.$key!)
        } else {
            this.googleMapComponent.setHoverParkingKey(null)
        }
    }

    onBoundsChange($event: LatLngBounds) {
        // console.log([$event.getCenter().lat(), $event.getCenter().lng()])
        this.currentBounds = $event
        const radiusInKm = distanceBetween([$event.getNorthEast().lat(), $event.getNorthEast().lng()], [$event.getCenter().lat(), $event.getCenter().lng()])
        let radiusInM = radiusInKm * 1000 * 0.4
        if (radiusInM > this.maxRadiusInMeters) {
            radiusInM = this.maxRadiusInMeters
        }

        const bounds = geohashQueryBounds([$event.getCenter().lat(), $event.getCenter().lng()], radiusInM)
        const promises = []
        for (const b of bounds) {
            promises.push(this.getParkingByGeoHash(b))
        }

        Promise.all(promises)
            .then((snapshots) => {
                const matchingParkings: Parking[] = []

                for (const snap of snapshots) {
                    for (const doc of snap.docs) {
                        const parking = new Parking(doc.data())
                        // console.log('Before filters: '+ parking.parkingType)
                        parking.$key = doc.id
                        if (this.showParking(parking) && this.hasTerm(parking, this.term)) {
                            // console.log('After filters: '+ parking.parkingType)
                            const distanceInKm = distanceBetween([parking.parkingLocation.latitude, parking.parkingLocation.longitude], [$event.getCenter().lat(), $event.getCenter().lng()])
                            let distanceInM = distanceInKm * 1000 * 0.4
                            if (distanceInM > this.maxRadiusInMeters) {
                                distanceInM = this.maxRadiusInMeters
                            }
                            if (distanceInM <= radiusInM) {
                                matchingParkings.push(parking)
                            }
                        }
                    }
                }
                // TODO sort by distance from center
                return matchingParkings
            })
            .then((matchingParkings) => {
                this.onDisplayParkings(this.filterTypes(matchingParkings))
            })
    }

    showParking(parking: Parking): boolean {
        for (const status of this.displayParkingsWithStatus) {
            if (parking.parkingStatus == status) {
                return true
            }
        }
        return false
    }

    filterTypes(parkings: Parking[]): Parking[] {
        if (this.selectedType) {
            return parkings.filter((parking) => parking.parkingType == this.selectedType)
        }
        return parkings
    }

    hasTerm(parking: Parking, term: Term | null) {
        return true
        // if (!parking.plans) {
        //     return false
        // }
        // if (!term) {
        //     return true
        // }
        // for (const plan of parking.plans) {
        //     if (plan.term == term) {
        //         return true
        //     }
        // }
        // return false
    }

    getParkingByGeoHash(bounds: string[]) {
        return new Promise<any>((resolve, reject) => {
            onSnapshot(
                query(collection(this.firestore, parkingsPath()), orderBy('parkingLocation.geoHash'), startAt(bounds[0]), endAt(bounds[1])),
                (snapshot) => {
                    resolve(snapshot)
                },
                (error) => {
                    reject(error)
                }
            )
        })
    }

    currentPosition = 0
    hideNavigation = false

    onWindowScroll(event: any) {
        let newYPos = document.getElementById('results-list')!!.scrollTop

        this.hideNavigation = newYPos > this.currentPosition && newYPos > 80
        this.currentPosition = newYPos
    }

    prettyType(type: any): any {
        switch (type) {
            case 'STANDARD':
                return 'ParkUpp Monthly'
            case 'PARKING_COLLECTOR':
                return 'ParkUpp Collector'
            case 'PARKING_EVENTS':
                return 'ParkUpp Events'
            case 'FLEX':
                return 'ParkUpp Flex'
        }
    }
}
